<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="subscription-detail-page">
    <div class="container-fluid">
        <!-- Loading State -->
        <div class="loading-state" *ngIf="loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Loading subscription details...</span>
        </div>

        <ng-container *ngIf="!loading && subscription">
            <!-- Page Header -->
            <div class="page-header">
                <div class="page-header__left">
                    <a routerLink="/admin/subscriptions" class="back-link">
                        <i class="fa-solid fa-arrow-left"></i> Back to Subscriptions
                    </a>
                    <h1>Subscription Details</h1>
                </div>
                <div class="page-header__actions">
                    <a *ngIf="subscription.isAdminEditable"
                       class="btn btn-secondary"
                       [routerLink]="['/admin/subscriptions-manual/edit', subscription.$key]">
                        <i class="fa-solid fa-edit"></i> Edit Details
                    </a>
                    <span *ngIf="!subscription.isAdminEditable" class="edit-locked" title="{{ subscription.paymentMechanismName }} subscriptions are backed by a payment mandate and cannot be edited">
                        <i class="fa-solid fa-lock"></i> {{ subscription.paymentMechanismName }} — not editable
                    </span>
                </div>
            </div>

            <!-- First payment needs attention (PayGate unavailable after automatic retries) -->
            <div *ngIf="firstPaymentNeedsAttention" class="alert alert-danger">
                <h4 class="alert-heading"><i class="fa-solid fa-triangle-exclamation"></i> Payment needs attention</h4>
                <p class="mb-2">
                    The landlord accepted this request, but PayGate was unavailable on every automatic attempt. Retry the payment now, or cancel the request (the parker and landlord get the
                    "payment couldn't be taken" emails).
                </p>
                <div *ngIf="!firstPaymentConfirm">
                    <button class="btn btn-primary me-2" type="button" [disabled]="firstPaymentBusy" (click)="firstPaymentConfirm = 'retry'">Retry payment now</button>
                    <button class="btn btn-outline-danger" type="button" [disabled]="firstPaymentBusy" (click)="firstPaymentConfirm = 'cancel'">Cancel request</button>
                </div>
                <div *ngIf="firstPaymentConfirm">
                    <p class="mb-2">
                        <strong>{{ firstPaymentConfirm === 'retry' ? 'Charge the first month on the parker\'s card now?' : 'Cancel this request and email both parties?' }}</strong>
                    </p>
                    <button class="btn btn-danger me-2" type="button" [disabled]="firstPaymentBusy" (click)="runFirstPaymentAction()">
                        <i *ngIf="firstPaymentBusy" class="fa-solid fa-spinner fa-spin"></i> Yes, {{ firstPaymentConfirm === 'retry' ? 'retry payment' : 'cancel request' }}
                    </button>
                    <button class="btn btn-secondary" type="button" [disabled]="firstPaymentBusy" (click)="firstPaymentConfirm = null">No, go back</button>
                </div>
            </div>
            <div *ngIf="firstPaymentMessage" class="alert" [ngClass]="firstPaymentError ? 'alert-warning' : 'alert-success'">{{ firstPaymentMessage }}</div>

            <!-- Detail Content -->
            <div class="detail-content">
                <!-- First payment (charge on landlord approval) -->
                <div class="detail-section" *ngIf="showFirstPaymentPanel">
                    <h3><i class="fa-solid fa-credit-card"></i> First Payment</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <span class="detail-label">Charge on approval</span>
                            <span class="detail-value">{{ subscription.chargeOnApproval ? 'Yes' : 'No (legacy hold)' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Landlord accepted</span>
                            <span class="detail-value">{{ subscription.landlordAcceptedAt ? (subscription.landlordAcceptedAt.toDate() | date: 'medium') : '—' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Attempts</span>
                            <span class="detail-value">{{ subscription.firstPaymentAttempts || 0 }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Next automatic retry</span>
                            <span class="detail-value">{{ subscription.firstPaymentRetryAt ? (subscription.firstPaymentRetryAt.toDate() | date: 'medium') : '—' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Escalated</span>
                            <span class="detail-value">{{ subscription.firstPaymentEscalatedAt ? (subscription.firstPaymentEscalatedAt.toDate() | date: 'medium') : '—' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Failed</span>
                            <span class="detail-value">
                                <ng-container *ngIf="subscription.firstPaymentFailedAt">
                                    {{ subscription.firstPaymentFailedAt.toDate() | date: 'medium' }} — {{ subscription.firstPaymentFailureReason }}
                                </ng-container>
                                <ng-container *ngIf="!subscription.firstPaymentFailedAt">—</ng-container>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Overview -->
                <div class="detail-section">
                    <h3><i class="fa-solid fa-file-contract"></i> Overview</h3>
                    <div class="detail-grid">
                        <div class="detail-item detail-item--full">
                            <span class="detail-label">Subscription ID</span>
                            <span class="detail-value detail-value--mono">{{ subscription.$key }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Status</span>
                            <span class="status-badge" [ngClass]="'status-badge--' + subscription.status?.toLowerCase()">
                                {{ subscription.statusNiceName }}
                            </span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Type</span>
                            <span class="type-badge" [ngClass]="'type-badge--' + subscription.type?.toLowerCase()">
                                {{ EnumNames.getSubscriptionType(subscription.type) }}
                            </span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Bays</span>
                            <span class="detail-value">{{ subscription.baysBooked }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Duration</span>
                            <span class="detail-value">{{ subscription.period }} {{ subscription.termNamePlural }}</span>
                        </div>
                    </div>
                </div>

                <!-- Financial Info -->
                <div class="detail-section">
                    <h3><i class="fa-solid fa-coins"></i> Financial</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <span class="detail-label">Monthly Fee</span>
                            <span class="detail-value detail-value--highlight">R{{ subscription.periodFee | formatCents }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Total Value</span>
                            <span class="detail-value">R{{ subscription.periodFee * subscription.period | formatCents }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Next Debit</span>
                            <span class="detail-value">{{ subscription.nextDebitDate | formatDate }}</span>
                        </div>
                        <div class="detail-item" *ngIf="subscription.renewalStatus">
                            <span class="detail-label">Renewal</span>
                            <span class="renewal-badge" [ngClass]="'renewal-badge--' + subscription.renewalStatus?.toLowerCase()">
                                {{ EnumNames.getRenewalStatus(subscription.renewalStatus) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Client Info -->
                <div class="detail-section" *ngIf="subscription.$client || subscription.manualClientName">
                    <h3><i class="fa-solid fa-user"></i> Client</h3>
                    <div class="detail-card" *ngIf="subscription.$client">
                        <div class="detail-card__main">
                            <span class="detail-card__name">{{ subscription.$client.firstName }} {{ subscription.$client.lastName }}</span>
                            <span class="detail-card__sub" *ngIf="subscription.$client.$email">{{ subscription.$client.$email }}</span>
                            <span class="detail-card__sub" *ngIf="subscription.$client.$phoneNumber">{{ subscription.$client.$phoneNumber }}</span>
                        </div>
                        <a class="detail-card__link" [routerLink]="['/client', subscription.clientRef?.id]">
                            <i class="fa-solid fa-external-link"></i>
                        </a>
                    </div>
                    <div class="detail-item" *ngIf="subscription.manualClientName" style="margin-top: 12px">
                        <span class="detail-label">Legacy Client Name</span>
                        <span class="detail-value">{{ subscription.manualClientName }}</span>
                    </div>
                </div>

                <!-- Parking Info -->
                <div class="detail-section" *ngIf="subscription.$parking">
                    <h3><i class="fa-solid fa-square-parking"></i> Parking</h3>
                    <div class="detail-card">
                        <div class="detail-card__main">
                            <span class="detail-card__name">{{ subscription.$parking.title }}</span>
                            <span class="detail-card__sub" *ngIf="subscription.$parking.parkingLocation?.city">
                                {{ subscription.$parking.parkingLocation.city }}
                            </span>
                        </div>
                        <a class="detail-card__link" [routerLink]="['/parking', subscription.parkingRef?.id]">
                            <i class="fa-solid fa-external-link"></i>
                        </a>
                    </div>
                </div>

                <!-- Dates -->
                <div class="detail-section">
                    <h3><i class="fa-solid fa-calendar"></i> Dates</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <span class="detail-label">Created</span>
                            <span class="detail-value">{{ subscription.createdAt | formatDate }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">Start</span>
                            <span class="detail-value">{{ subscription.startDate | customDate: 'D MMM YYYY' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">End</span>
                            <span class="detail-value">{{ subscription.endDate | customDate: 'D MMM YYYY' }}</span>
                        </div>
                        <div class="detail-item" *ngIf="subscription.daysUntilExpiry">
                            <span class="detail-label">Days Until Expiry</span>
                            <span class="detail-value">{{ subscription.daysUntilExpiry }} days</span>
                        </div>
                    </div>
                </div>

                <!-- Source / Campaign -->
                <div class="detail-section" *ngIf="subscription.p_source || subscription.p_campaign">
                    <h3><i class="fa-solid fa-bullhorn"></i> Attribution</h3>
                    <div class="detail-grid">
                        <div class="detail-item" *ngIf="subscription.p_source">
                            <span class="detail-label">Source</span>
                            <span class="detail-value">{{ subscription.p_source }}</span>
                        </div>
                        <div class="detail-item" *ngIf="subscription.p_campaign">
                            <span class="detail-label">Campaign</span>
                            <span class="detail-value">{{ subscription.p_campaign }}</span>
                        </div>
                    </div>
                </div>

                <!-- Timeline -->
                <div class="detail-section detail-section--full" *ngIf="events.length > 0">
                    <h3><i class="fa-solid fa-timeline"></i> Timeline</h3>
                    <div class="timeline">
                        <div class="timeline-item" *ngFor="let event of events">
                            <div class="timeline-dot" [ngClass]="'timeline-dot--' + (event.type || 'default')"></div>
                            <div class="timeline-content">
                                <span class="timeline-title">{{ event.title }}</span>
                                <span class="timeline-date">{{ event.date }}</span>
                                <span class="timeline-desc" *ngIf="event.description">{{ event.description }}</span>
                                <ul class="timeline-changes" *ngIf="event.changes?.length">
                                    <li *ngFor="let change of event.changes">
                                        <span class="timeline-changes__label">{{ change.label }}</span>
                                        <span class="timeline-changes__from">{{ change.from }}</span>
                                        <i class="fa-solid fa-arrow-right"></i>
                                        <span class="timeline-changes__to">{{ change.to }}</span>
                                    </li>
                                </ul>
                                <span class="timeline-actor" *ngIf="event.actor">by {{ event.actor }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ng-container>
    </div>
</div>
